// ---------------------------------------------------------------------------
// ladder.js — the four-rung benchmark engine (source of truth).
//
// Replaces the old +5pp/+1pp uplift math in classifier.js. For each lever
// (engagement E, reach R, conversion C) it resolves an ORDERED LADDER of
// benchmark rungs, picks the ACTIVE target (the lowest rung above current,
// an attainable stretch), and sizes the "within reach" € to that rung.
//
//   Rung 1  Your best weeks   — robust top-quartile-median of this zone's own
//                               trailing weekly series (persisted by the ETL:
//                               DetectionZoneSettings.Rung1_Engagement / _Reach).
//                               C has NO Rung 1 (it's a static admin estimate).
//   Rung 2  Your best zone    — top-quartile across your same-type (product)
//                               zones, for E / R / C.
//   Rung 3  Peer cohort       — p80 across comparable stores (sector + size
//                               band). OPTIONAL: null until the cross-company
//                               cohort has ≥ min stores; the resolver handles
//                               its absence with no consumer change.
//   Rung 4  Ceiling / horizon — research Emax / Cmax; reach = 1.0 (arithmetic
//                               cap, 100% of store footfall — no research Tmax).
//
// The design lives in docs/06_four_rung_benchmark_model.md (§2). This module
// is the Phase-1 realisation of §2.2 / §2.3 / §2.4 with the optional Rung-3
// hook wired from the start (Phase 3).
//
// PURE + framework-free so it can be unit-tested without mounting components
// and mirrored line-for-line by backend/applicaite/signals/ladder.py. Every
// numeric knob comes from settings.js (→ prioritization_config.json) so JS
// and Python stay on identical constants.
// ---------------------------------------------------------------------------

import { LADDER } from './settings.js'

// Levers. Conversion is flagged `est` because it rides on the manual
// DetectionZoneSettings estimate (no POS) until the Guidance sales feed lands.
//
// ceilingActive — may Rung 4 be the ACTIVE target? Only ENGAGEMENT: its Rung 4
// is the research Emax, a distant-but-real best-in-class rung that guarantees
// "always a rung above" (§2.2), and engagement is DIRECTLY MEASURED so the climb
// is earned. For REACH, Rung 4 is 1.0 (100% of footfall) — an ARITHMETIC cap,
// not an attainable goal: a store's reach leader must not be told to "reach +77pp
// to 100%". For CONVERSION, Rung 4 is the research Cmax, but conversion is a
// STATIC admin estimate (no POS, no weekly series), so "+33pp to best-in-class"
// is an UNEARNED comparative claim (the very thing the platform forbids) — an
// overshoot, since there's no near rung between the store's own zones and Cmax.
// So BOTH reach and conversion keep Rung 4 HORIZON-ONLY (the outer display tick,
// never an active target): a zone above its near rungs (own-best / best-zone /
// cohort) is at its frontier → no € for that lever until a real near rung exists.
// This makes conversion self-heal — the moment a comparable 2nd store lights the
// peer cohort, its Rung 3 becomes the attainable target automatically (§2.1/§2.3).
// requiresRung1 — reach is an active lever ONLY once the zone has an own-best
// reach baseline (Rung1_Reach, calibrated). Without it we don't push a zone to
// "match the busiest zone" (an unattainable, position-bound target — docs/12);
// reach's cross-zone/cohort rungs stay horizon-only until own-best exists. This
// makes own-best the genuine floor that bounds reach €.
export const LEVERS = {
  engagement: { key: 'engagement', kind: 'eng', est: false, hasRung1: true, ceilingActive: true },
  reach: { key: 'reach', kind: 'reach', est: false, hasRung1: true, ceilingActive: false, requiresRung1: true },
  conversion: { key: 'conversion', kind: 'conv', est: true, hasRung1: false, ceilingActive: false },
}

// ---------------------------------------------------------------------------
// Robust cross-zone statistics (Rung 2)
// ---------------------------------------------------------------------------

// Top-quartile order statistic (75th percentile, linear interpolation between
// order statistics — same 'linear' method the ETL percentile uses). This is
// the "your best zone" value that isn't set by a single outlier zone (§2.2:
// "top-quartile across those zones so one outlier doesn't set it"). We use it
// for all three levers rather than the raw max the table loosely calls "best",
// following the note's explicit anti-outlier rationale.
export function topQuartileValue(values) {
  const xs = (values || []).filter((v) => Number.isFinite(v)).sort((a, b) => a - b)
  const n = xs.length
  if (n === 0) return null
  if (n === 1) return xs[0]
  const pos = 0.75 * (n - 1)
  const lo = Math.floor(pos)
  const hi = Math.min(lo + 1, n - 1)
  const frac = pos - lo
  return xs[lo] + (xs[hi] - xs[lo]) * frac
}

// ---------------------------------------------------------------------------
// Rung resolution (§2.2)
// ---------------------------------------------------------------------------

/**
 * Resolve the four rung VALUES for one lever. Any rung may be null (missing
 * data / not applicable); the active-target selector skips nulls.
 *
 * @param lever    one of LEVERS.*
 * @param ctx      { rung1, peers: number[], ceiling } — rung1 is the persisted
 *                 own-best value (null if none / not applicable); peers is the
 *                 same-type cross-zone value list for THIS lever; ceiling is
 *                 Rung 4 (Emax / Cmax / 1.0).
 * @param cohort   optional Rung-3 value (p80 across comparable stores) or null.
 */
export function resolveRungs(lever, ctx, cohort = null) {
  const r1 = lever.hasRung1 && isRate(ctx.rung1) ? ctx.rung1 : null
  const tq = topQuartileValue(ctx.peers)   // bind once (mirrors ladder.py)
  const r2 = isRate(tq) ? tq : null
  const r3 = isRate(cohort) ? cohort : null
  const r4 = isRate(ctx.ceiling) ? ctx.ceiling : null
  return { r1, r2, r3, r4 }
}

function isRate(x) {
  return Number.isFinite(x) && x >= 0 && x <= 1
}

// ---------------------------------------------------------------------------
// Active-target selection (§2.3)
// ---------------------------------------------------------------------------

/**
 * Pick the active target = lowest rung strictly above (current + MARGIN).
 * Rungs are compared BY VALUE so non-monotonic cases resolve correctly (if
 * your own best already exceeds the cohort p80, the cohort simply isn't the
 * next rung). When current is at/above every rung, there is no level target
 * → atCeiling, and the caller surfaces the consistency lever instead.
 *
 * Returns { active, activeName, atCeiling, horizon: [{name, value}] }.
 */
export function selectActive(current, rungs, margin = LADDER.MARGIN) {
  const NAMES = { r1: 'your best weeks', r2: 'your best zones', r3: 'peer cohort', r4: 'best-in-class' }
  const ordered = ['r1', 'r2', 'r3', 'r4']
    .map((k) => ({ name: NAMES[k], key: k, value: rungs[k] }))
    .filter((r) => r.value != null)
    .sort((a, b) => a.value - b.value)

  const above = ordered.filter((r) => r.value > current + margin)
  if (above.length === 0) {
    // Already at/above the top available rung → hold / consistency.
    const top = ordered.length ? ordered[ordered.length - 1] : null
    return { active: top ? top.value : null, activeName: top ? top.name : null, atCeiling: true, horizon: [] }
  }
  const active = above[0]
  return {
    active: active.value,
    activeName: active.name,
    atCeiling: false,
    horizon: above.slice(1).map((r) => ({ name: r.name, value: r.value })),
  }
}

// ---------------------------------------------------------------------------
// Within-reach € per lever (§2.4)
// ---------------------------------------------------------------------------

/**
 * € unlocked by moving current → active for one lever, carried through the
 * REST of the funnel (elasticity = 1 for every factor, so each is a clean
 * marginal-gap × downstream-multipliers product):
 *
 *   €_engagement = (E_active − E_cur) × visitors        × C_cur × avg
 *   €_conversion = (C_active − C_cur) × visitors × E_cur         × avg   (est.)
 *   €_reach      = (R_active − R_cur) × storeVisitors × E_cur × C_cur × avg
 *
 * `z` carries E_cur/C_cur/R_cur, visitors, storeVisitors, avg.
 */
// platform_capabilities.md §17.7 Phase 2 — measurability-aware. A counts-only (tripwire) zone can't
// measure engagement, so its engagement/conversion € funnel is undefined (0) and
// its REACH opportunity is expressed as a VISITORS count (gap × store footfall),
// NOT ×engagement×conversion (which are 0 → the old bug that collapsed every leg
// to €0). The caller stamps the leg's unit.
export function withinReachEUR(lever, z, active) {
  if (!Number.isFinite(active)) return 0
  const avg = z.avgValue || 0
  const measurable = z.engagementMeasurable !== false
  if (lever.key === 'engagement') {
    if (!measurable) return 0
    const gap = Math.max(0, active - z.engagementRate)
    return gap * z.visitors * z.conversionRate * avg
  }
  if (lever.key === 'conversion') {
    if (!measurable) return 0
    const gap = Math.max(0, active - z.conversionRate)
    return gap * z.visitors * z.engagementRate * avg
  }
  // reach
  const gap = Math.max(0, active - z.reachRate)
  if (!measurable) return gap * z.storeVisitors   // visitors-first: a COUNT, no € funnel
  return gap * z.storeVisitors * z.engagementRate * z.conversionRate * avg
}

// ---------------------------------------------------------------------------
// Full ladder for one zone (§2.2–2.4 assembled)
// ---------------------------------------------------------------------------

/**
 * @param z       zone context:
 *                { engagementRate, conversionRate, reachRate, visitors,
 *                  storeVisitors, avgValue,
 *                  rung1Engagement, rung1Reach }   // persisted own-best (or null)
 * @param peers   { engagement:number[], reach:number[], conversion:number[] }
 *                same-type cross-zone value lists (this zone MAY be included;
 *                top-quartile is robust to it).
 * @param ceilings { Emax, Cmax }  Rung-4 research ceilings (reach uses 1.0).
 * @param cohort  optional { engagement, reach, conversion } p80 values, each
 *                nullable — Rung 3. Absent/null until the cohort is populated.
 *
 * Returns per-lever ladders plus the headline (biggest-€ lever, its range and
 * target rung) — the thing the nightly recommender action targets.
 */
export function computeLadder(z, peers, ceilings, cohort = null) {
  peers = peers || { engagement: [], reach: [], conversion: [] }
  cohort = cohort || {}

  let reachLeg = buildLeg(LEVERS.reach, z.reachRate, {
    rung1: z.rung1Reach, peers: peers.reach, ceiling: 1.0,
  }, cohort.reach, z)
  // Reach gated OFF (docs/12): a confirmed destination category / back-corner
  // position isn't a reach problem — suppress reach as an active lever so it
  // can't headline (or dominate). Keep the rungs for display; zero the €.
  if (z.reachGated) {
    reachLeg = { ...reachLeg, active: null, activeName: null, atCeiling: true, eur: 0, magnitude: 0, range: [0, 0], deltaPp: 0, gated: true }
  }

  const legs = {
    engagement: buildLeg(LEVERS.engagement, z.engagementRate, {
      rung1: z.rung1Engagement, peers: peers.engagement, ceiling: ceilings?.Emax,
    }, cohort.engagement, z),
    reach: reachLeg,
    // Conversion graduates from modelled "(est.)" to a hard lever once the store
    // feeds sales (z.conversionMeasured) — drops the est band + qualifier.
    conversion: buildLeg(
      z.conversionMeasured ? { ...LEVERS.conversion, est: false } : LEVERS.conversion,
      z.conversionRate, {
        rung1: null, peers: peers.conversion, ceiling: ceilings?.Cmax,
      }, cohort.conversion, z),
  }

  // Headline = the actionable lever (not at ceiling) with the largest € to its
  // active rung. Ties broken engagement > reach > conversion (measured first,
  // conversion is the shaky estimate). If every lever is at ceiling, headline
  // is null and the consistency framing takes over.
  const order = ['engagement', 'reach', 'conversion']
  let headline = null
  for (const k of order) {
    const leg = legs[k]
    // platform_capabilities.md §17.7 Phase 2 — rank by MAGNITUDE (€ for a measured funnel, visitors for a
    // counts-only reach) so a footfall zone's reach opportunity can headline.
    if (leg.atCeiling || !(leg.magnitude > 0)) continue
    if (!headline || leg.magnitude > headline.magnitude) {
      headline = {
        lever: k, kind: LEVERS[k].kind, est: leg.est,
        eur: leg.eur, magnitude: leg.magnitude, unit: leg.unit,
        range: leg.range, targetRung: leg.activeName, target: leg.active,
      }
    }
  }

  return {
    legs,
    headline,
    atCeiling: !headline,
    // platform_capabilities.md §17.7 Phase 1 — carried so patternFromLadder can classify a counts-only
    // zone on reach/traffic terms instead of falsely calling it "performing well".
    engagementMeasurable: z.engagementMeasurable !== false,
  }
}

function buildLeg(lever, current, ctx, cohortVal, z) {
  const rungs = resolveRungs(lever, ctx, cohortVal ?? null)
  // Rung 4 is horizon-only for reach (1.0 arithmetic cap) AND conversion (Cmax —
  // an unearned claim on a static estimate): exclude it from active selection so
  // a frontier zone reads "at frontier", not "+to the ceiling". The full `rungs`
  // (incl. r4) is still returned for the outer display tick.
  let selRungs = lever.ceilingActive ? rungs : { ...rungs, r4: null }
  // Reach follows ONE rule for every geometry. A tripwire zone used to have both
  // gates below relaxed, so it was benchmarked against other zones' reach wherever
  // they sat — exactly the back-aisle-vs-end-cap comparison docs/12 forbids. It now
  // gets an own-best (Rung1_Reach is calibrated from its tripwire counts), so it can
  // be held to the same standard as a polygon zone.
  // requiresRung1 (reach): no own-best baseline → no active target (all rungs
  // horizon-only), so reach can't jump to an unattainable best-zone target.
  if (lever.requiresRung1 && rungs.r1 == null) selRungs = { r1: null, r2: null, r3: null, r4: null }
  // Reach's cross-zone rungs (best-zone / cohort) are trusted as ACTIVE targets
  // only when traffic-exposure is CONFIRMED (we know the zones are position-
  // comparable — docs/12). Otherwise reach is own-best-only, so a niche zone is
  // never benchmarked against a hero zone that merely shares the default group.
  if (lever.key === 'reach' && !z.reachPeersActive) selRungs = { ...selRungs, r2: null, r3: null }
  // Reach's margin is the zone's OWN week-to-week wobble (std. dev. of its weekly
  // reach, calibrated with Rung1_Reach): a gap only counts when it is bigger than
  // the zone's normal swing. Falls back to the fixed MARGIN only until the first
  // calibration that writes the wobble.
  const reachWobble = Number(z.reachWobble)
  const margin = (lever.key === 'reach' && z.reachWobble != null && Number.isFinite(reachWobble) && reachWobble >= 0)
    ? reachWobble : LADDER.MARGIN
  const sel = selectActive(current, selRungs, margin)
  // Unit: a counts-only zone's reach opportunity is a VISITORS count, not €.
  const measurable = z.engagementMeasurable !== false
  const unit = (lever.key === 'reach' && !measurable) ? 'visitors' : 'eur'
  const magRaw = sel.atCeiling ? 0 : withinReachEUR(lever, z, sel.active)
  const mag = Math.round(magRaw)
  const eur = unit === 'eur' ? mag : 0
  // Widen the ± band for conversion to honour the estimate's uncertainty.
  const bandPct = lever.est ? LADDER.RANGE_PCT_EST : LADDER.RANGE_PCT
  const range = mag > 0 ? [Math.round(mag * (1 - bandPct)), Math.round(mag * (1 + bandPct))] : [0, 0]
  return {
    current,
    rungs,
    active: sel.active,
    activeName: sel.activeName,
    atCeiling: sel.atCeiling,
    horizon: sel.horizon,
    eur,
    // platform_capabilities.md §17.7 Phase 2 — unit-aware magnitude so the headline can pick a leg whose
    // opportunity is in visitors (reach) as well as one in € (funnel).
    magnitude: mag,
    unit,
    range,
    est: lever.est,
    // Attainable delta to the active rung, in percentage points — replaces the
    // old fixed "+5pp/+1pp" copy with "+Xpp to reach <rung>".
    deltaPp: sel.atCeiling ? 0 : Math.max(0, (sel.active - current)) * 100,
  }
}
